import React from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import styled, { css, keyframes, useTheme } from 'styled-components';
import IconButton, { ICON_BUTTON_OPTIONS } from '../../atoms/IconButton';
import useModalLogic from './useModalLogic';

const MODAL_CONSTS = {
  CLOSE_MODAL_BUTTON_COLOR: ICON_BUTTON_OPTIONS.COLORS.NORMAL,
  CLOSE_MODAL_BUTTON_ICON: 'generalxlarge',
  CLOSE_MODAL_BUTTON_SIZE: ICON_BUTTON_OPTIONS.SIZES.XXL,
};

const slideDown = keyframes`
  0% {
    transform: translate(0, -100%);
  }
  100% {
    transform: translate(0, 0);
  }
`;

const slideUp = keyframes`
  0% {
    transform: translate(0, 0);
  }
  100% {
    transform: translate(0, -100%);
  }
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }`;

const fadeOut = keyframes`
0% {
  opacity: 1;
}
100% {
  opacity: 0;
}`;

const StyledBlackout = styled.div`
  background-color: rgba(0, 0, 0, 0.4);
  height: 100%;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 9999;

  /** ANIMATE ENTRANCE/EXIT OF MODAL */
  ${props =>
    props.show
      ? css`
          animation: ${fadeIn} ${props.enteringAnimationDuration}ms
            ${props.theme.transition.timingFunction.easeIn} 0s 1 normal forwards
            running;
        `
      : css`
          animation: ${fadeOut} ${props.exitAnimationDuration}ms
            ${props.theme.transition.timingFunction.easeOut} 0s 1 normal
            forwards running;
        `}
`;

const StyledContainer = styled.div`
  background: ${props => props.theme.colors.white};
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 99999;

  /** ANIMATE ENTRANCE/EXIT OF MODAL */
  ${props =>
    props.show
      ? css`
          animation: ${slideDown} ${props.enteringAnimationDuration}ms
            ${props.theme.transition.timingFunction.easeIn} 0s 1 normal forwards
            running;
        `
      : css`
          animation: ${slideUp} ${props.exitAnimationDuration}ms
            ${props.theme.transition.timingFunction.easeOut} 0s 1 normal
            forwards running;
        `}
`;

const StyledIconButton = styled(IconButton)`
  padding: 5px;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 2;
`;

const Modal = ({ children, className }) => {
  const theme = useTheme();
  const enteringAnimationDuration = theme.transition.duration.enteringScreen;
  const exitAnimationDuration = theme.transition.duration.leavingScreen;
  const {
    handleBlackoutClick,
    handleCloseModalButtonClick,
    show,
  } = useModalLogic({ exitAnimationDuration });

  return createPortal(
    <>
      <StyledBlackout
        enteringAnimationDuration={enteringAnimationDuration}
        exitAnimationDuration={exitAnimationDuration}
        onClick={handleBlackoutClick}
        show={show}
      />
      <StyledContainer
        enteringAnimationDuration={enteringAnimationDuration}
        exitAnimationDuration={exitAnimationDuration}
        show={show}
      >
        <StyledIconButton
          color={MODAL_CONSTS.CLOSE_MODAL_BUTTON_COLOR}
          handleClick={handleCloseModalButtonClick}
          icon={MODAL_CONSTS.CLOSE_MODAL_BUTTON_ICON}
          size={MODAL_CONSTS.CLOSE_MODAL_BUTTON_SIZE}
        />
        <div className={className}>{children}</div>
      </StyledContainer>
    </>,
    document.body
  );
};

const modalPropTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};
const modalDefaultProps = {
  className: undefined,
};

Modal.propTypes = modalPropTypes;
Modal.defaultProps = modalDefaultProps;
export default Modal;
export { modalDefaultProps, modalPropTypes };
